import React, { useState, useEffect } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Bannière de statut de paiement Stripe après retour du checkout
const PaymentStatusBanner = () => {
  const [paymentState, setPaymentState] = useState(null); // 'checking', 'success', 'expired', 'error', 'timeout', 'cancelled'
  const [productTitle, setProductTitle] = useState('');

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get('session_id');
    const payment = params.get('payment');

    if (payment === 'cancelled') {
      setPaymentState('cancelled');
      window.history.replaceState({}, document.title, window.location.pathname);
      return;
    }
    if (sessionId && payment === 'retrocession') {
      setPaymentState('checking');
      window.history.replaceState({}, document.title, window.location.pathname);
      pollStatus(sessionId, 0);
    }
  }, []);

  const pollStatus = async (sessionId, attempts) => {
    const maxAttempts = 8;
    if (attempts >= maxAttempts) {
      setPaymentState('timeout');
      return;
    }
    try {
      const response = await axios.get(`${API}/retrocession/checkout/status/${sessionId}`);
      const data = response.data;
      if (data.payment_status === 'paid') {
        setProductTitle(data.produit_titre || '');
        setPaymentState('success');
        return;
      }
      if (data.status === 'expired') {
        setPaymentState('expired');
        return;
      }
      setTimeout(() => pollStatus(sessionId, attempts + 1), 2000);
    } catch (error) {
      setPaymentState('error');
    }
  };

  if (!paymentState) return null;

  const configs = {
    checking: { bg: 'bg-blue-600/20 border-blue-500/40 text-blue-300', icon: '⏳', msg: 'Vérification du paiement en cours...' },
    success: { bg: 'bg-emerald-600/20 border-emerald-500/40 text-emerald-300', icon: '✅', msg: `Paiement réussi ! ${productTitle ? `Le produit "${productTitle}" est à vous.` : 'Votre achat est confirmé.'} Retrouvez-le dans "Mes Transactions".` },
    expired: { bg: 'bg-orange-600/20 border-orange-500/40 text-orange-300', icon: '⚠️', msg: 'La session de paiement a expiré. Veuillez réessayer.' },
    error: { bg: 'bg-red-600/20 border-red-500/40 text-red-300', icon: '❌', msg: 'Erreur lors de la vérification du paiement. Contactez le support si vous avez été débité.' },
    timeout: { bg: 'bg-orange-600/20 border-orange-500/40 text-orange-300', icon: '⚠️', msg: 'Vérification du paiement trop longue. Consultez "Mes Transactions" pour vérifier.' },
    cancelled: { bg: 'bg-slate-600/20 border-slate-500/40 text-slate-300', icon: 'ℹ️', msg: 'Paiement annulé. Le produit reste disponible.' }
  };
  const c = configs[paymentState];

  return (
    <div data-testid="payment-status-banner" className={`border rounded-xl p-4 mb-6 flex items-center space-x-3 ${c.bg}`}>
      <span className="text-2xl">{c.icon}</span>
      <p className="font-medium">{c.msg}</p>
    </div>
  );
};

export const RetrocessionManagement = () => {
  const [activeTab, setActiveTab] = useState('browse');

  const tabs = [
    { id: 'browse', label: 'Parcourir', icon: '🔍' },
    { id: 'sell', label: 'Vendre', icon: '💰' },
    { id: 'request', label: 'Demander', icon: '🙋‍♂️' },
    { id: 'transactions', label: 'Mes Transactions', icon: '📊' }
  ];

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-2xl font-bold text-white mb-4 flex items-center">
          <span className="mr-3">🔄</span>
          Rétrocession - Marché entre Magasins
        </h3>
        <p className="text-slate-400 mb-6">
          Vendez votre stock invendu et trouvez les produits dont vous avez besoin auprès d'autres magasins d'optique
        </p>

        <PaymentStatusBanner />
        
        {/* Navigation tabs */}
        <div className="bg-gradient-to-r from-slate-800/50 to-slate-700/50 border border-slate-600/50 rounded-xl p-4 mb-6">
          <div className="flex space-x-1 overflow-x-auto">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-all duration-200 whitespace-nowrap ${
                  activeTab === tab.id
                    ? 'bg-blue-600/30 text-blue-300 border border-blue-500/30'
                    : 'text-slate-400 hover:text-white hover:bg-white/10'
                }`}
              >
                <span>{tab.icon}</span>
                <span>{tab.label}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Contenu */}
        <div>
          {activeTab === 'browse' && <BrowseProducts />}
          {activeTab === 'sell' && <SellProduct />}
          {activeTab === 'request' && <RequestProduct />}
          {activeTab === 'transactions' && <MyTransactions />}
        </div>
      </div>
    </div>
  );
};

// Composant pour parcourir les produits disponibles
const BrowseProducts = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    categorie: '',
    marque: '',
    prix_max: '',
    ville: ''
  });

  useEffect(() => {
    fetchProducts();
  }, [filters]);

  const fetchProducts = async () => {
    try {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([key, value]) => {
        if (value) params.append(key, value);
      });
      
      const response = await axios.get(`${API}/retrocession/produits?${params.toString()}`);
      setProducts(response.data);
    } catch (error) {
      console.error('Erreur lors du chargement:', error);
    } finally {
      setLoading(false);
    }
  };

  const [buyingId, setBuyingId] = useState(null);

  const handleBuy = async (productId) => {
    setBuyingId(productId);
    try {
      const response = await axios.post(`${API}/retrocession/checkout`, {
        produit_id: productId,
        origin_url: window.location.origin,
        mode_livraison: "retrait"
      });
      if (response.data.url) {
        window.location.href = response.data.url;
      }
    } catch (error) {
      alert(error.response?.data?.detail || 'Erreur lors de la création du paiement');
      setBuyingId(null);
    }
  };

  return (
    <div className="space-y-6">
      {/* Filtres */}
      <div className="bg-gradient-to-br from-slate-800/50 to-slate-700/50 border border-slate-600/50 rounded-xl p-6">
        <h4 className="text-lg font-semibold text-white mb-4">🎛️ Filtres de recherche</h4>
        <div className="grid md:grid-cols-4 gap-4">
          <select
            value={filters.categorie}
            onChange={(e) => setFilters(prev => ({ ...prev, categorie: e.target.value }))}
            className="px-4 py-3 bg-slate-600/50 border border-slate-500/50 rounded-lg text-white"
          >
            <option value="">Toutes catégories</option>
            <option value="monture">Montures</option>
            <option value="verre">Verres</option>
            <option value="accessoire">Accessoires</option>
          </select>
          
          <input
            type="text"
            placeholder="Marque..."
            value={filters.marque}
            onChange={(e) => setFilters(prev => ({ ...prev, marque: e.target.value }))}
            className="px-4 py-3 bg-slate-600/50 border border-slate-500/50 rounded-lg text-white placeholder-slate-400"
          />
          
          <input
            type="number"
            placeholder="Prix max..."
            value={filters.prix_max}
            onChange={(e) => setFilters(prev => ({ ...prev, prix_max: e.target.value }))}
            className="px-4 py-3 bg-slate-600/50 border border-slate-500/50 rounded-lg text-white placeholder-slate-400"
          />
          
          <input
            type="text"
            placeholder="Ville..."
            value={filters.ville}
            onChange={(e) => setFilters(prev => ({ ...prev, ville: e.target.value }))}
            className="px-4 py-3 bg-slate-600/50 border border-slate-500/50 rounded-lg text-white placeholder-slate-400"
          />
        </div>
      </div>

      {/* Liste des produits */}
      {loading ? (
        <div className="flex items-center justify-center p-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
        </div>
      ) : products.length === 0 ? (
        <div className="bg-slate-700/30 rounded-xl p-12 text-center border border-slate-600/30">
          <div className="text-6xl mb-4">📦</div>
          <h3 className="text-xl font-semibold text-white mb-2">Aucun produit disponible</h3>
          <p className="text-slate-400">
            Aucun produit ne correspond à vos critères pour le moment.
          </p>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {products.map((product) => (
            <ProductCard key={product.id} product={product} onBuy={handleBuy} buying={buyingId === product.id} />
          ))}
        </div>
      )}
    </div>
  );
};

// Carte produit
const ProductCard = ({ product, onBuy, buying }) => {
  const getMotifColor = (motif) => {
    switch(motif) {
      case 'invendu': return 'bg-orange-600/20 text-orange-400';
      case 'commande_erreur': return 'bg-red-600/20 text-red-400';
      case 'fin_de_collection': return 'bg-purple-600/20 text-purple-400';
      default: return 'bg-gray-600/20 text-gray-400';
    }
  };

  const getMotifLabel = (motif) => {
    switch(motif) {
      case 'invendu': return 'Invendu';
      case 'commande_erreur': return 'Erreur commande';
      case 'fin_de_collection': return 'Fin collection';
      default: return motif;
    }
  };

  return (
    <div className="bg-gradient-to-br from-slate-800/50 to-slate-700/50 border border-slate-600/50 rounded-xl p-6 shadow-lg hover:shadow-xl transition-all duration-300">
      <div className="flex justify-between items-start mb-4">
        <h4 className="text-lg font-semibold text-white">{product.titre}</h4>
        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getMotifColor(product.motif_retrocession)}`}>
          {getMotifLabel(product.motif_retrocession)}
        </span>
      </div>
      
      <div className="space-y-3 mb-6">
        <div className="flex justify-between text-sm">
          <span className="text-slate-400">Marque:</span>
          <span className="text-white font-medium">{product.marque} {product.modele}</span>
        </div>
        <div className="flex justify-between text-sm">
          <span className="text-slate-400">Catégorie:</span>
          <span className="text-white">{product.categorie}</span>
        </div>
        <div className="flex justify-between text-sm">
          <span className="text-slate-400">État:</span>
          <span className="text-white">{product.etat}</span>
        </div>
        <div className="flex justify-between text-sm">
          <span className="text-slate-400">Quantité:</span>
          <span className="text-white">{product.quantite_disponible}</span>
        </div>
        <div className="flex justify-between text-sm">
          <span className="text-slate-400">Prix d'achat initial:</span>
          <span className="text-slate-400 line-through">{product.prix_achat_initial}€</span>
        </div>
      </div>

      <div className="border-t border-slate-600/30 pt-4 mb-4">
        <p className="text-slate-300 text-sm mb-3">{product.description}</p>
        {product.fournisseur && (
          <p className="text-slate-400 text-xs">Fournisseur: {product.fournisseur}</p>
        )}
      </div>

      <div className="flex justify-between items-center">
        <div>
          <p className="text-2xl font-bold text-emerald-400">{product.prix_retrocession}€</p>
          <p className="text-xs text-slate-400">Prix de rétrocession</p>
        </div>
        <button
          data-testid={`buy-product-btn-${product.id}`}
          onClick={() => onBuy(product.id)}
          disabled={buying}
          className="bg-gradient-to-r from-emerald-600 to-green-600 hover:from-emerald-700 hover:to-green-700 disabled:opacity-60 disabled:cursor-wait text-white px-6 py-3 rounded-lg transition-all duration-300 font-medium"
        >
          {buying ? 'Redirection...' : '💳 Acheter'}
        </button>
      </div>
    </div>
  );
};

// Composant pour vendre un produit
const SellProduct = () => {
  const [formData, setFormData] = useState({
    titre: '',
    description: '',
    categorie: 'monture',
    marque: '',
    modele: '',
    prix_achat_initial: '',
    prix_retrocession: '',
    quantite_disponible: 1,
    etat: 'neuf',
    fournisseur: '',
    motif_retrocession: 'invendu',
    caracteristiques: {}
  });
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const productData = {
        ...formData,
        prix_achat_initial: parseFloat(formData.prix_achat_initial),
        prix_retrocession: parseFloat(formData.prix_retrocession),
        quantite_disponible: parseInt(formData.quantite_disponible)
      };

      await axios.post(`${API}/retrocession/produits`, productData);
      alert('Produit mis en vente avec succès !');
      
      // Reset form
      setFormData({
        titre: '', description: '', categorie: 'monture', marque: '', modele: '',
        prix_achat_initial: '', prix_retrocession: '', quantite_disponible: 1,
        etat: 'neuf', fournisseur: '', motif_retrocession: 'invendu', caracteristiques: {}
      });
      
    } catch (error) {
      setError(error.response?.data?.detail || 'Une erreur est survenue');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type } = e.target;
    setFormData(prev => ({ 
      ...prev, 
      [name]: type === 'number' ? value : value
    }));
  };

  return (
    <div className="bg-gradient-to-br from-slate-800/50 to-slate-700/50 border border-slate-600/50 rounded-xl p-8 shadow-xl">
      <h4 className="text-xl font-bold text-white mb-6 flex items-center">
        <span className="mr-3">💰</span>
        Vendre un produit en rétrocession
      </h4>
      <p className="text-slate-400 mb-6">
        Vous avez du stock que vous n'arrivez pas à écouler ? Proposez-le à d'autres magasins au prix d'achat.
      </p>

      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="grid md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-slate-200 mb-2">
              Titre du produit *
            </label>
            <input
              type="text"
              name="titre"
              value={formData.titre}
              onChange={handleInputChange}
              className="w-full px-4 py-3 bg-slate-600/50 border border-slate-500/50 rounded-lg text-white placeholder-slate-400"
              placeholder="Ex: Monture Ray-Ban RB2140 Noire"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-200 mb-2">
              Catégorie *
            </label>
            <select
              name="categorie"
              value={formData.categorie}
              onChange={handleInputChange}
              className="w-full px-4 py-3 bg-slate-600/50 border border-slate-500/50 rounded-lg text-white"
            >
              <option value="monture">Monture</option>
              <option value="verre">Verre</option>
              <option value="accessoire">Accessoire</option>
            </select>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-slate-200 mb-2">
              Marque *
            </label>
            <input
              type="text"
              name="marque"
              value={formData.marque}
              onChange={handleInputChange}
              className="w-full px-4 py-3 bg-slate-600/50 border border-slate-500/50 rounded-lg text-white placeholder-slate-400"
              placeholder="Ray-Ban, Oakley, etc."
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-200 mb-2">
              Modèle *
            </label>
            <input
              type="text"
              name="modele"
              value={formData.modele}
              onChange={handleInputChange}
              className="w-full px-4 py-3 bg-slate-600/50 border border-slate-500/50 rounded-lg text-white placeholder-slate-400"
              placeholder="RB2140, etc."
              required
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-slate-200 mb-2">
            Description *
          </label>
          <textarea
            name="description"
            value={formData.description}
            onChange={handleInputChange}
            rows="3"
            className="w-full px-4 py-3 bg-slate-600/50 border border-slate-500/50 rounded-lg text-white placeholder-slate-400"
            placeholder="Décrivez l'état, les caractéristiques, etc."
            required
          />
        </div>

        <div className="grid md:grid-cols-3 gap-6">
          <div>
            <label className="block text-sm font-medium text-slate-200 mb-2">
              Prix d'achat initial *
            </label>
            <input
              type="number"
              name="prix_achat_initial"
              value={formData.prix_achat_initial}
              onChange={handleInputChange}
              step="0.01"
              className="w-full px-4 py-3 bg-slate-600/50 border border-slate-500/50 rounded-lg text-white"
              placeholder="120.00"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-200 mb-2">
              Prix de rétrocession *
            </label>
            <input
              type="number"
              name="prix_retrocession"
              value={formData.prix_retrocession}
              onChange={handleInputChange}
              step="0.01"
              className="w-full px-4 py-3 bg-slate-600/50 border border-slate-500/50 rounded-lg text-white"
              placeholder="120.00"
              required
            />
            <p className="text-xs text-slate-400 mt-1">Généralement = prix d'achat</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-200 mb-2">
              Quantité
            </label>
            <input
              type="number"
              name="quantite_disponible"
              value={formData.quantite_disponible}
              onChange={handleInputChange}
              min="1"
              className="w-full px-4 py-3 bg-slate-600/50 border border-slate-500/50 rounded-lg text-white"
            />
          </div>
        </div>

        <div className="grid md:grid-cols-3 gap-6">
          <div>
            <label className="block text-sm font-medium text-slate-200 mb-2">
              État
            </label>
            <select
              name="etat"
              value={formData.etat}
              onChange={handleInputChange}
              className="w-full px-4 py-3 bg-slate-600/50 border border-slate-500/50 rounded-lg text-white"
            >
              <option value="neuf">Neuf</option>
              <option value="demonstration">Démonstration</option>
              <option value="leger_defaut">Léger défaut</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-200 mb-2">
              Motif de rétrocession
            </label>
            <select
              name="motif_retrocession"
              value={formData.motif_retrocession}
              onChange={handleInputChange}
              className="w-full px-4 py-3 bg-slate-600/50 border border-slate-500/50 rounded-lg text-white"
            >
              <option value="invendu">Invendu</option>
              <option value="commande_erreur">Erreur de commande</option>
              <option value="fin_de_collection">Fin de collection</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-200 mb-2">
              Fournisseur
            </label>
            <input
              type="text"
              name="fournisseur"
              value={formData.fournisseur}
              onChange={handleInputChange}
              className="w-full px-4 py-3 bg-slate-600/50 border border-slate-500/50 rounded-lg text-white placeholder-slate-400"
              placeholder="Nom du fournisseur"
            />
          </div>
        </div>

        {error && (
          <div className="p-4 bg-red-600/20 border border-red-500/30 text-red-200 rounded-lg">
            {error}
          </div>
        )}

        <button
          type="submit"
          disabled={loading}
          className="w-full bg-gradient-to-r from-emerald-600 to-green-600 hover:from-emerald-700 hover:to-green-700 text-white font-medium py-4 px-6 rounded-lg transition-all duration-300 disabled:opacity-50"
        >
          {loading ? (
            <div className="flex items-center justify-center space-x-2">
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
              <span>Publication...</span>
            </div>
          ) : (
            'Mettre en vente'
          )}
        </button>
      </form>
    </div>
  );
};

// Composants placeholder pour les autres onglets
const RequestProduct = () => (
  <div className="bg-slate-700/30 rounded-xl p-8 text-center border border-slate-600/30">
    <div className="text-4xl mb-4">🙋‍♂️</div>
    <h4 className="text-lg font-semibold text-white mb-2">Demandes de produits</h4>
    <p className="text-slate-400">
      Fonctionnalité à venir : Publiez vos demandes de produits spécifiques.
    </p>
  </div>
);

const MyTransactions = () => (
  <div className="bg-slate-700/30 rounded-xl p-8 text-center border border-slate-600/30">
    <div className="text-4xl mb-4">📊</div>
    <h4 className="text-lg font-semibold text-white mb-2">Mes transactions</h4>
    <p className="text-slate-400">
      Fonctionnalité à venir : Suivez vos achats et ventes en rétrocession.
    </p>
  </div>
);